import type { EpisodeManifest, NewsReportingBasis } from "./stage-api";
import { validReportingInstant, developmentNoticeAsOf } from "./news-development-notice";

const bounded = (value: unknown, limit: number): value is string => typeof value === "string" &&
  value.length > 0 && value.length <= limit && value === value.trim() && !/[\x00-\x1f\x7f]/.test(value);
const safeHttps = (value: string): boolean => {
  try { const url = new URL(value); return url.protocol === "https:" && !url.username && !url.password; }
  catch { return false; }
};

// Validate existing disclosure/source bindings, never publisher trust or story admission.
export function reportingBasisFor(episode: EpisodeManifest): NewsReportingBasis | null {
  const basis = episode.newsCoverage?.reportingBasis;
  if ((episode.slot !== "morning" && episode.slot !== "afternoon") || !basis ||
      !["trusted-single-source", "claim-specific-v1"].includes(basis.kind) ||
      !bounded(basis.primarySourceUrl, 4096) || !safeHttps(basis.primarySourceUrl) ||
      !bounded(basis.publisherId, 100) || !bounded(basis.publisherLabel, 80) ||
      !bounded(basis.publisherPolicyVersion, 100) || !validReportingInstant(basis.asOfUtc)) return null;
  const sources = new Set(episode.sources.map(source => source.url));
  if (!sources.has(basis.primarySourceUrl)) return null;
  if (basis.kind === "trusted-single-source") {
    if (basis.claims != null || episode.sources.length !== 1) return null;
  } else if (!Array.isArray(basis.claims) || basis.claims.length < 1 || basis.claims.length > 7 ||
      basis.claims.some(claim => !claim || !bounded(claim.text, 8000) ||
        !["low-risk", "institutional-statement", "independent-required"].includes(claim.supportRequirement) ||
        !Array.isArray(claim.citations) || claim.citations.length < 1 || claim.citations.length > 6 ||
        claim.citations.some(citation => !citation || !bounded(citation.excerpt, 8000) ||
          !bounded(citation.sourceUrl, 4096) || !safeHttps(citation.sourceUrl) || !sources.has(citation.sourceUrl)))) return null;
  return basis;
}

export function reportingBasisAsOf(basis: NewsReportingBasis): string {
  return developmentNoticeAsOf({ text: "", asOfUtc: basis.asOfUtc });
}
